/**
 * DistributionDemoContext — Shared session state for Phase 0 demo
 * =================================================================
 *
 * One in-memory client state that powers the connected demo story:
 *
 *   1. Release to Retail         → user publishes to retail channels
 *                                  + runs Intelligent Command Center
 *                                  insights (Resolve / Explore)
 *   2. Inventory Control Tower   → user runs the What-If Simulator
 *                                  (demand +25/50/100%, new order,
 *                                  lead-time, warehouse transfer)
 *                                  + makes per-line allocation calls
 *   3. Retail Order Automation   → the simulated world drives the
 *                                  Smart Replenishment Recommendation
 *                                  cards (standard / expedited /
 *                                  transfer / split) and the Approve
 *                                  PO flow (simulated only)
 *
 * All simulation math lives in `useDistributionSimulation` below so
 * every page sees the same numbers. The context never writes to the
 * server — refreshing the page resets the demo.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';

const DistributionDemoContext = createContext(null);

// Base assumptions kept inline so the simulation doesn't depend on
// `/intelligence` having been fetched yet. The real values come from
// the backend fixture — this is just the fallback at bootstrap.
const DEFAULT_LEAD_TIME_DAYS = 56;

export const DistributionDemoProvider = ({ children }) => {
    // ─── Step 1: publishing state ───────────────────────────────
    const [publishedChannels, setPublishedChannels] = useState({});
    const [publishedAt, setPublishedAt] = useState(null);

    // ─── Step 2: allocation decisions ───────────────────────────
    // Keyed `${order_id}:${sku}` → "allocated" | "backorder" | null
    const [allocationDecisions, setAllocationDecisions] = useState({});

    // ─── Step 3: replenishment approvals + strategy ─────────────
    // suggestion_id → boolean approved
    const [approvedReplenishments, setApprovedReplenishments] = useState({});
    // suggestion_id → option_id (which card from Smart Recommendations)
    const [selectedStrategy, setSelectedStrategy] = useState({});

    // ─── What-If Simulator state ────────────────────────────────
    const [demandMultiplier, setDemandMultiplier] = useState(1.0);
    const [hypotheticalOrder, setHypotheticalOrder] = useState(null); // { qty, sku, retailer }
    const [leadTimeOverride, setLeadTimeOverride] = useState(null); // null | days
    const [warehouseTransfer, setWarehouseTransfer] = useState(null); // { from, to, sku, qty }

    // ─── Insights acknowledged by the user ──────────────────────
    // NOTE: we do NOT track "resolved" as a user-clickable flag.
    // Resolution is derived from the live simulation — see
    // computeInsightStatus() below.
    const [acknowledgedInsights, setAcknowledgedInsights] = useState({});

    // ─── Highlight affected supply-chain nodes briefly ──────────
    const [highlightedNodes, setHighlightedNodes] = useState([]);
    const flashNodes = (nodeIds) => {
        setHighlightedNodes(nodeIds || []);
        if (!nodeIds || nodeIds.length === 0) return;
        setTimeout(() => setHighlightedNodes([]), 2200);
    };

    const resetSimulation = () => {
        setDemandMultiplier(1.0);
        setHypotheticalOrder(null);
        setLeadTimeOverride(null);
        setWarehouseTransfer(null);
    };

    const value = useMemo(() => ({
        // publishing
        publishedChannels,
        setPublishedChannels,
        publishedAt,
        setPublishedAt,
        publishedChannelCount: Object.values(publishedChannels).filter(Boolean).length,
        isPublished: Boolean(publishedAt),

        // allocation
        allocationDecisions,
        setAllocationDecisions,
        allocatedCount: Object.values(allocationDecisions).filter(v => v === 'allocated').length,

        // replenishment
        approvedReplenishments,
        setApprovedReplenishments,
        selectedStrategy,
        setSelectedStrategy,
        approvedCount: Object.values(approvedReplenishments).filter(Boolean).length,

        // simulation
        demandMultiplier,
        setDemandMultiplier,
        hypotheticalOrder,
        setHypotheticalOrder,
        leadTimeOverride,
        setLeadTimeOverride,
        warehouseTransfer,
        setWarehouseTransfer,
        resetSimulation,
        defaultLeadTimeDays: DEFAULT_LEAD_TIME_DAYS,
        effectiveLeadTimeDays: leadTimeOverride ?? DEFAULT_LEAD_TIME_DAYS,
        isSimulating:
            demandMultiplier !== 1.0 ||
            hypotheticalOrder !== null ||
            leadTimeOverride !== null ||
            warehouseTransfer !== null,

        // insights
        acknowledgedInsights,
        setAcknowledgedInsights,
        acknowledgeInsight: (id) => setAcknowledgedInsights(prev => ({ ...prev, [id]: true })),
        unacknowledgeInsight: (id) => setAcknowledgedInsights(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        }),

        // UX
        highlightedNodes,
        flashNodes,
    }), [
        publishedChannels, publishedAt,
        allocationDecisions,
        approvedReplenishments, selectedStrategy,
        demandMultiplier, hypotheticalOrder, leadTimeOverride, warehouseTransfer,
        acknowledgedInsights, highlightedNodes,
    ]);

    return (
        <DistributionDemoContext.Provider value={value}>
            {children}
        </DistributionDemoContext.Provider>
    );
};

export const useDistributionDemo = () => {
    const ctx = useContext(DistributionDemoContext);
    if (!ctx) {
        throw new Error('useDistributionDemo must be used inside DistributionDemoProvider');
    }
    return ctx;
};


/**
 * useDistributionSimulation — single source of truth for live math.
 *
 * Takes the base variants + sell-through from the backend fixture and
 * returns per-SKU projections that honour the current simulation state
 * (demand multiplier, hypothetical order, warehouse transfer, allocated
 * units) PLUS the current effective lead time. Every page reads from
 * this helper so there is only ONE place that can get the numbers
 * wrong — and if it changes, all three screens update together.
 *
 * @param {Array}  variants      - from /release or /inventory fixture
 * @param {Array}  sellThrough   - from /replenishment or /intelligence
 * @param {Array}  warehouses    - from /intelligence (may be empty)
 */
export const useDistributionSimulation = (variants = [], sellThrough = [], warehouses = []) => {
    const {
        demandMultiplier,
        hypotheticalOrder,
        warehouseTransfer,
        allocationDecisions,
        effectiveLeadTimeDays,
    } = useDistributionDemo();

    return useMemo(() => {
        const sellBySku = Object.fromEntries(sellThrough.map(s => [s.sku, s]));
        const variantBySku = Object.fromEntries(variants.map(v => [v.sku, v]));

        // Count allocated units per SKU from the shared decisions map.
        // Keys look like `${order_id}:${sku}` — grab the SKU after the colon.
        const consumed = {};
        Object.entries(allocationDecisions).forEach(([key, decision]) => {
            if (decision !== 'allocated') return;
            const sku = key.split(':').slice(-1)[0];
            consumed[sku] = (consumed[sku] || 0) + 1; // qty handled inside pages
        });

        const projections = variants.map(v => {
            const sell = sellBySku[v.sku];
            const base = sell?.avg_daily_units || 0;
            const effectiveVelocity = base * demandMultiplier;

            // Stock reserved by this session's allocations (page-level
            // code already tracks true qty — this helper rolls up the
            // high-level view only).
            const transferIn = (warehouseTransfer && warehouseTransfer.sku === v.sku) ? warehouseTransfer.qty : 0;
            const hypoOut = (hypotheticalOrder && hypotheticalOrder.sku === v.sku) ? hypotheticalOrder.qty : 0;

            const digital = v.on_hand === null;
            const effectiveStock = digital
                ? null
                : Math.max(0, (v.on_hand || 0) + transferIn - hypoOut);

            const daysCover = digital
                ? null
                : effectiveVelocity > 0 ? Math.round(effectiveStock / effectiveVelocity) : null;

            const supplyGapDays = daysCover === null
                ? 0
                : Math.max(0, effectiveLeadTimeDays - daysCover);

            // Business impact (mock domain): units we would miss during
            // the gap × the retail price (what the distribution network
            // leaves on the table). Keep conservative — only vinyl /
            // physical SKUs can go out of stock.
            const unitsAtRisk = Math.round(supplyGapDays * effectiveVelocity);
            const revenueAtRisk = digital ? 0 : unitsAtRisk * (v.wholesale_price_eur || 0);

            return {
                sku: v.sku,
                format: v.format,
                digital,
                baseVelocity: base,
                effectiveVelocity,
                baseOnHand: v.on_hand,
                effectiveStock,
                daysCover,
                supplyGapDays,
                unitsAtRisk,
                revenueAtRisk,
                hypoOut,
                transferIn,
                retailPrice: v.retail_price_eur,
                wholesalePrice: v.wholesale_price_eur,
            };
        });

        const totalRevenueAtRisk = projections.reduce((s, p) => s + p.revenueAtRisk, 0);
        const stockoutsDetected = projections.filter(p => p.supplyGapDays > 0).length;

        // Idle inventory at secondary warehouses (mocked, static-ish)
        const secondaryWarehouse = warehouses.find(w => w.primary === false);
        const idleAtSecondary = secondaryWarehouse
            ? Object.entries(secondaryWarehouse.stock_by_sku || {})
                  .filter(([, qty]) => qty && qty > 0)
                  .reduce((s, [sku, qty]) => s + qty * (variantBySku[sku]?.wholesale_price_eur || 0), 0)
            : 0;

        return {
            projections,
            totalRevenueAtRisk,
            stockoutsDetected,
            idleAtSecondary,
        };
    }, [variants, sellThrough, warehouses, demandMultiplier, hypotheticalOrder, warehouseTransfer, allocationDecisions, effectiveLeadTimeDays]);
};



/**
 * Status lifecycle (user-approved):
 *
 *   detected            — Issue identified (default when a real problem exists)
 *   action_recommended  — Solution available (Owntune has at least one recommended action)
 *   acknowledged        — User clicked the primary CTA but no simulation effect
 *                         could be applied (used for informational insights)
 *   action_selected     — User has picked a strategy but not yet approved
 *   simulated           — Impact has been calculated and applied to the sim
 *   partially_mitigated — Simulated action reduced the risk but did not eliminate it
 *   resolved_simulated  — Updated simulation confirms the issue is eliminated
 *                         (never set purely from a click — always derived from math)
 */
export const INSIGHT_STATUSES = {
    detected:            { label: 'Detected',             tone: 'rose'   },
    action_recommended:  { label: 'Action recommended',   tone: 'indigo' },
    acknowledged:        { label: 'Acknowledged',         tone: 'slate'  },
    action_selected:     { label: 'Action selected',      tone: 'amber'  },
    simulated:           { label: 'Simulated',            tone: 'indigo' },
    partially_mitigated: { label: 'Partially mitigated',  tone: 'amber'  },
    resolved_simulated:  { label: 'Resolved (simulated)', tone: 'emerald' },
};

/**
 * Compute the status for an insight from the live simulation state.
 * NEVER set "resolved_simulated" or "partially_mitigated" from a user
 * click — only from a measurable change in the projection.
 */
export const computeInsightStatus = (insight, {
    projection,
    acknowledged,
    warehouseTransfer,
    leadTimeOverride,
    selectedStrategyForSku,
    approvedForSku,
    hasOptions,
}) => {
    const sku = insight.sku;

    switch (insight.impact_type) {
        case 'revenue_at_risk': {
            // Gap-type insight. Walks the user through the full ladder.
            if (!projection) return hasOptions ? 'action_recommended' : 'detected';
            const baseGap = projection.supplyGapDays;

            // Nothing simulated against this SKU yet
            const anyEffect = approvedForSku || selectedStrategyForSku || (warehouseTransfer && warehouseTransfer.sku === sku) || leadTimeOverride !== null;

            if (!anyEffect) {
                return hasOptions ? 'action_recommended' : 'detected';
            }

            if (approvedForSku) {
                if (baseGap === 0) return 'resolved_simulated';
                return 'partially_mitigated';
            }

            // Transfer/leadtime applied via What-If, no approval yet
            const simApplied = (warehouseTransfer && warehouseTransfer.sku === sku) || leadTimeOverride !== null;
            if (simApplied) {
                if (baseGap === 0) return 'resolved_simulated';
                return 'simulated';
            }

            if (selectedStrategyForSku) return 'action_selected';
            return 'action_recommended';
        }

        case 'transfer_opportunity': {
            if (!warehouseTransfer || warehouseTransfer.sku !== sku) {
                return hasOptions ? 'action_recommended' : 'detected';
            }
            // Transfer applied — did it close the gap?
            if (!projection) return 'simulated';
            if (projection.supplyGapDays === 0) return 'resolved_simulated';
            return 'partially_mitigated';
        }

        case 'channel_rebalance':
        case 'attach_opportunity':
            // No simulation hook — only reaches "acknowledged" after click.
            return acknowledged ? 'acknowledged' : 'action_recommended';

        default:
            return 'detected';
    }
};

/**
 * Apply the measurable effect of a replenishment strategy to the shared
 * simulation state. Called on Approve. Returns a short label describing
 * what was simulated so UI can toast it.
 */
export const applyStrategyEffectHelpers = {
    expedited: (option, setLeadTimeOverride) => {
        setLeadTimeOverride(option.lead_time_days);
        return `Lead time dropped to ${option.lead_time_days} days`;
    },
    transfer: (option, suggestion, setWarehouseTransfer) => {
        const qty = Math.min(suggestion.suggested_qty, option.qty_cap || suggestion.suggested_qty);
        setWarehouseTransfer({ sku: suggestion.sku, qty, from: 'nordisk-no', to: 'gls-se' });
        return `${qty} units transferred Oslo → Sweden`;
    },
};
